import { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useAppStore } from '@/store/appStore';
import { User, Building2, LogOut, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, logout } = useAuthStore();
  const { currentOrg } = useAppStore();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white font-['Manrope']">Settings</h1>
        <p className="text-zinc-400 text-sm mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <User className="w-5 h-5" /> Profile
        </h2>

        <div className="flex items-start gap-6">
          <Avatar className="w-20 h-20">
            <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-violet-500 text-2xl font-bold text-white">
              {user?.name?.charAt(0).toUpperCase() || '?'}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">Full Name</label>
              <Input
                value={user?.name || ''}
                disabled
                className="bg-zinc-950 border-zinc-800 text-zinc-300"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">Email Address</label>
              <Input
                value={user?.email || ''}
                disabled
                className="bg-zinc-950 border-zinc-800 text-zinc-300"
                data-testid="profile-email-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Organization Section */}
      {currentOrg && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Current Organization
          </h2>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">{currentOrg.name}</p>
              <p className="text-sm text-zinc-400 mt-1">{currentOrg.description || 'No description'}</p>
            </div>
            <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
              <Shield className="w-3 h-3 mr-1" />
              {currentOrg.user_role?.replace('_', ' ') || 'Member'}
            </Badge>
          </div>
        </div>
      )}

      {/* Account Section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Account</h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-zinc-950 rounded-lg">
            <div>
              <p className="text-white font-medium">Account Created</p>
              <p className="text-sm text-zinc-400">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </p>
            </div>
          </div>

          <Separator className="bg-zinc-800" />

          <div className="pt-4">
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
